/**
 * ChoiceFlow state management with reducer pattern
 * Handles flow navigation, field answers, and form state
 */

/**
 * Flow action types
 */
export const FLOW_ACTIONS = Object.freeze({
  ANSWER_FIELD: 'ANSWER_FIELD',
  GOTO_PAGE: 'GOTO_PAGE',
  GO_BACK: 'GO_BACK',
  SET_BUSY: 'SET_BUSY',
  SET_ERROR: 'SET_ERROR',
  RESET_FLOW: 'RESET_FLOW',
  HYDRATE_STATE: 'HYDRATE_STATE'
});

/**
 * Initial flow state factory
 * @param {string} startPageId - Starting page identifier
 * @returns {object} Initial state object
 */
export const createInitialState = (startPageId) => ({
  currentPageId: startPageId,
  visitedPageIds: [],
  answers: {},
  isBusy: false,
  error: null,
  branch: null
});

/**
 * Prune answers for pages/fields that come after the specified page
 * This is used when going back to ensure downstream answers are cleared
 * @param {object} answers - Current answers
 * @param {object} flowData - Complete flow data
 * @param {string} currentPageId - Current page ID
 * @returns {object} Pruned answers
 */
export const pruneDownstreamAnswers = (answers, flowData, currentPageId) => {
  if (!flowData?.pages) {
    return answers;
  }

  // Find current page index
  const currentPageIndex = flowData.pages.findIndex((page) => page.id === currentPageId);
  if (currentPageIndex === -1) {
    return answers;
  }

  // Get field IDs from pages that come after the current page
  const downstreamFieldIds = new Set();
  for (let i = currentPageIndex + 1; i < flowData.pages.length; i += 1) {
    const page = flowData.pages[i];
    if (page.kind === 'form_step' && page.fields) {
      page.fields.forEach((field) => {
        downstreamFieldIds.add(field.id);
      });
    }
  }

  // Remove downstream field answers
  const prunedAnswers = { ...answers };
  downstreamFieldIds.forEach((fieldId) => {
    delete prunedAnswers[fieldId];
  });

  return prunedAnswers;
};

/**
 * Check if answers match a condition object
 * @param {object} condition - Condition to check (simple key-value matches)
 * @param {object} answers - Current answers
 * @returns {boolean} Whether condition matches
 */
export const matchesCondition = (condition, answers) => {
  if (!condition || typeof condition !== 'object') {
    return false;
  }

  return Object.entries(condition).every(([key, value]) => {
    return answers[key] === value;
  });
};

/**
 * Get visible pages based on current answers (for future conditional page visibility)
 * @param {Array} pages - Array of pages
 * @param {object} answers - Current answers
 * @returns {Array} Visible pages
 */
export const getVisiblePages = (pages) => {
  // For now, all pages are visible - this can be enhanced later
  // for conditional page visibility based on answers
  return pages;
};

/**
 * Get visible fields based on visible_when conditions
 * @param {Array} fields - Array of field definitions
 * @param {object} answers - Current answers
 * @returns {Array} Visible fields
 */
export const getVisibleFields = (fields, answers) => {
  return fields.filter((field) => {
    if (!field.visible_when) {
      return true; // Field is visible by default
    }

    // Check if field should be visible based on conditions
    return matchesCondition(field.visible_when, answers);
  });
};

/**
 * Flow state reducer
 * @param {object} state - Current state
 * @param {object} action - Action object with type and payload
 * @returns {object} New state
 */
export const flowReducer = (state, action) => {
  switch (action.type) {
    case FLOW_ACTIONS.ANSWER_FIELD: {
      const { fieldId, value } = action.payload;
      return {
        ...state,
        answers: {
          ...state.answers,
          [fieldId]: value
        },
        error: null // Clear errors when user interacts
      };
    }

    case FLOW_ACTIONS.GOTO_PAGE: {
      const { pageId, addToHistory = true } = action.payload;
      const newVisitedPageIds =
        addToHistory && !state.visitedPageIds.includes(state.currentPageId)
          ? [...state.visitedPageIds, state.currentPageId]
          : state.visitedPageIds;

      return {
        ...state,
        currentPageId: pageId,
        visitedPageIds: newVisitedPageIds,
        isBusy: false,
        error: null
      };
    }

    case FLOW_ACTIONS.GO_BACK: {
      if (state.visitedPageIds.length === 0) {
        return state; // Can't go back further
      }

      const previousPageId = state.visitedPageIds[state.visitedPageIds.length - 1];
      const newVisitedPageIds = state.visitedPageIds.slice(0, -1);

      // Prune downstream answers when going back
      const prunedAnswers = pruneDownstreamAnswers(
        state.answers,
        action.payload.flowData,
        previousPageId
      );

      return {
        ...state,
        currentPageId: previousPageId,
        visitedPageIds: newVisitedPageIds,
        answers: prunedAnswers,
        isBusy: false,
        error: null
      };
    }

    case FLOW_ACTIONS.SET_BUSY: {
      return {
        ...state,
        isBusy: action.payload.isBusy,
        error: null
      };
    }

    case FLOW_ACTIONS.SET_ERROR: {
      return {
        ...state,
        error: action.payload.error,
        isBusy: false
      };
    }

    case FLOW_ACTIONS.RESET_FLOW: {
      const { startPageId } = action.payload;
      return createInitialState(startPageId);
    }

    case FLOW_ACTIONS.HYDRATE_STATE: {
      const { persistedState } = action.payload;
      return {
        ...state,
        ...persistedState,
        isBusy: false, // Always clear loading state on hydration
        error: null
      };
    }

    default:
      return state;
  }
};

/**
 * Resolve the next page based on current page's next rule
 * @param {object} currentPage - Current page object
 * @param {object} answers - Current form answers
 * @returns {string|null} Next page ID or null if no valid next page
 */
export const resolveNextPage = (currentPage, answers) => {
  if (!currentPage?.next) {
    return null;
  }

  const { next } = currentPage;

  // Simple goto
  if (next.goto) {
    return next.goto;
  }

  // Branching logic
  if (next.branch) {
    for (const branchRule of next.branch) {
      // Check if this is the default branch
      if (branchRule.default) {
        return branchRule.goto;
      }

      // Check if conditions match
      if (branchRule.when && matchesCondition(branchRule.when, answers)) {
        return branchRule.goto;
      }
    }

    // Fallback to first branch if no conditions match and no default
    return next.branch[0]?.goto || null;
  }

  // Server resolution (handled by parent component)
  if (next.server) {
    return 'SERVER_RESOLVE';
  }

  return null;
};

/**
 * Compute progress information for the flow
 * @param {object} state - Current flow state
 * @param {object} flowData - Complete flow data
 * @param {string} progressMode - Progress calculation mode ('pages' or 'required_fields')
 * @returns {object} Progress information
 */
export const computeProgress = (state, flowData, progressMode = 'pages') => {
  if (!flowData?.pages) {
    return { current: 0, total: 0, percentage: 0 };
  }

  const formStepPages = flowData.pages.filter((page) => page.kind === 'form_step');

  if (progressMode === 'pages') {
    const currentIndex = formStepPages.findIndex((page) => page.id === state.currentPageId);
    const total = formStepPages.length;
    const current = currentIndex >= 0 ? currentIndex + 1 : 1;

    return {
      current,
      total,
      percentage: total > 0 ? Math.round((current / total) * 100) : 0
    };
  }

  if (progressMode === 'required_fields') {
    // Calculate based on required fields answered vs total required fields
    const visiblePages = getVisiblePages(formStepPages, state.answers);
    const totalRequiredFields = visiblePages.reduce((count, page) => {
      const visibleFields = getVisibleFields(page.fields || [], state.answers);
      return count + visibleFields.filter((field) => field.required).length;
    }, 0);

    const answeredRequiredFields = visiblePages.reduce((count, page) => {
      const visibleFields = getVisibleFields(page.fields || [], state.answers);
      return (
        count +
        visibleFields.filter((field) => field.required && state.answers[field.id] != null).length
      );
    }, 0);

    return {
      current: answeredRequiredFields,
      total: totalRequiredFields,
      percentage:
        totalRequiredFields > 0
          ? Math.round((answeredRequiredFields / totalRequiredFields) * 100)
          : 0
    };
  }

  return { current: 0, total: 0, percentage: 0 };
};

/**
 * Action creators for common flow operations
 */
export const flowActions = {
  answerField: (fieldId, value) => ({
    type: FLOW_ACTIONS.ANSWER_FIELD,
    payload: { fieldId, value }
  }),

  gotoPage: (pageId, addToHistory = true) => ({
    type: FLOW_ACTIONS.GOTO_PAGE,
    payload: { pageId, addToHistory }
  }),

  goBack: (flowData) => ({
    type: FLOW_ACTIONS.GO_BACK,
    payload: { flowData }
  }),

  setBusy: (isBusy) => ({
    type: FLOW_ACTIONS.SET_BUSY,
    payload: { isBusy }
  }),

  setError: (error) => ({
    type: FLOW_ACTIONS.SET_ERROR,
    payload: { error }
  }),

  resetFlow: (startPageId) => ({
    type: FLOW_ACTIONS.RESET_FLOW,
    payload: { startPageId }
  }),

  hydrateState: (persistedState) => ({
    type: FLOW_ACTIONS.HYDRATE_STATE,
    payload: { persistedState }
  })
};
